import { Tooltip } from '@sgme/ui';
import classnames from 'classnames';
import { Checkbox } from 'components/shared/Checkbox/Checkbox';
import { FormattedMessage } from 'components/shared/Intl';
import { IntlKey } from 'locales';
import type { EmailServiceDocument } from 'models/emailService/EmailServiceDocument';
import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  getDocumentStatusIcon,
  getDocumentTooltipProps,
  isDocumentGenerationFailed,
  isDocumentValidated,
} from 'utils/documents/documentProperties';
import { camelCaseToWords } from 'utils/string';

export type DocumentListItemProps = EmailServiceDocument & {
  e2e?: string;
  className?: string;
  isSelected?: boolean;
  onSelected: (option: EmailServiceDocument) => void;
};

const StyledLiContainer = styled.li`
  &:not(.disabled) {
    cursor: pointer;

    .form-check:hover {
      label {
        background: unset;
      }
    }
  }
`;

const StyledLanguageDiv = styled.div`
  pointer-events: auto;
`;

// TODO: Unit tests
export const DocumentListItem: FC<DocumentListItemProps> = ({
  e2e,
  type,
  language,
  className,
  status,
  isSelected = false,
  onSelected,
}) => {
  const defaultDocumentName = useMemo(() => camelCaseToWords(type), [type]);

  const isGenerationFailed = useMemo(() => isDocumentGenerationFailed(status), [status]);
  const isNotValidated = useMemo(() => !isDocumentValidated(status), [status]);
  const isNotValidatedNorFailed = useMemo(
    () => status && !isDocumentValidated(status) && !isGenerationFailed,
    [status, isGenerationFailed],
  );

  const isDisabled = useMemo(() => isGenerationFailed || isNotValidated, [isGenerationFailed, isNotValidated]);
  const textClass = classnames({
    'text-danger': isGenerationFailed,
    'text-warning': isNotValidatedNorFailed,
    'text-secondary': !isGenerationFailed && !isNotValidatedNorFailed,
  });

  const withTooltipProps = useMemo(() => getDocumentTooltipProps(status, type, 'emailService'), [status, type]);
  const icon = getDocumentStatusIcon(status);

  const onChangeOrClick = useCallback(() => {
    if (!isDisabled) {
      onSelected({ type, language, status });
    }
  }, [type, language, status, isDisabled, onSelected]);

  return (
    <StyledLiContainer
      className={classnames('list-group-item list-group-item-action border-light', {
        'list-group-item-secondary disabled': isDisabled,
      })}
      onClick={onChangeOrClick}
    >
      <div className={classnames('d-flex align-items-center', className)} data-e2e={`${e2e}-document`}>
        <Checkbox disabled={isDisabled} checked={isSelected} onChange={onChangeOrClick} />
        <div className="d-flex align-items-center flex-fill" style={{ height: 26 }}>
          <i className="icon icon-sm me-2">insert_drive_file</i>
          <span data-e2e={`${e2e}-document-type`}>
            <FormattedMessage id={`emailService.documents.${type}`} defaultMessage={defaultDocumentName} />
          </span>
        </div>

        {(language !== 'Unknown' || icon !== undefined) && (
          <StyledLanguageDiv className="d-flex align-items-center">
            {language !== 'Unknown' && (
              <span className={textClass} data-e2e={`${e2e}-document-language`}>
                <FormattedMessage id={`languageCode.${language}` as any} defaultMessage={language} />
              </span>
            )}
            {icon && (
              <Tooltip
                position="top"
                contents={<FormattedMessage id={`${withTooltipProps.intlKey}` as IntlKey} />}
                color={withTooltipProps.color}
              >
                <div className={classnames('ms-1', textClass)}>{icon && <i className="icon icon-sm">{icon}</i>}</div>
              </Tooltip>
            )}
          </StyledLanguageDiv>
        )}
      </div>
    </StyledLiContainer>
  );
};
